import type { ScanFinding, GroupedFindings } from '../types/scan.ts';

export type SeverityLevel = 'critical' | 'high' | 'medium' | 'low' | 'info';

/**
 * Filter findings by severity levels
 */
export const filterFindingsBySeverity = (
  findings: ScanFinding[],
  selectedSeverities: SeverityLevel[]
): ScanFinding[] => {
  if (selectedSeverities.length === 0) return [];
  return findings.filter(finding => 
    selectedSeverities.includes(finding.severity as SeverityLevel)
  );
};

/**
 * Filter grouped findings by severity levels
 */
export const filterGroupedFindingsBySeverity = (
  groupedFindings: GroupedFindings,
  selectedSeverities: SeverityLevel[]
): GroupedFindings => {
  const filtered: GroupedFindings = {
    security: [],
    privacy: [],
    compliance: [],
    miscellaneous: [],
  };

  Object.entries(groupedFindings).forEach(([category, findings]) => {
    filtered[category as keyof GroupedFindings] = filterFindingsBySeverity(
      findings,
      selectedSeverities
    );
  });

  return filtered;
};

/**
 * Count findings by severity level
 */
export const countFindingsBySeverity = (
  findings: ScanFinding[]
): Record<SeverityLevel, number> => {
  const counts: Record<SeverityLevel, number> = {
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    info: 0,
  };

  findings.forEach(finding => {
    const severity = finding.severity as SeverityLevel;
    if (severity in counts) {
      counts[severity]++;
    }
  });

  return counts;
};

/**
 * Get total count of findings across all categories
 */
export const getTotalFindingsCount = (groupedFindings: GroupedFindings): number => {
  return Object.values(groupedFindings).reduce(
    (total, findings) => total + findings.length,
    0
  );
};

/**
 * Get severity breakdown across all findings
 */
export const getSeverityBreakdown = (
  groupedFindings: GroupedFindings
): Record<SeverityLevel, number> => {
  const allFindings = Object.values(groupedFindings).flat();
  return countFindingsBySeverity(allFindings);
};

/**
 * Check if any findings exist for given severity levels
 */
export const hasFindingsForSeverity = (
  groupedFindings: GroupedFindings,
  severities: SeverityLevel[]
): boolean => {
  const allFindings = Object.values(groupedFindings).flat();
  return allFindings.some(finding => 
    severities.includes(finding.severity as SeverityLevel)
  );
}; 